import api from './api';
import { umamiTrack } from './analytics';

export type TrackingEntityType = 'AD_BANNER' | 'NEWS' | 'EVENT' | 'PAGE' | 'PRO_RACE_STAGE';
export type TrackingAction = 'IMPRESSION' | 'CLICK' | 'VIEW' | 'DOWNLOAD';

/** Die Zahlen, die auf beiden Ebenen (Entität und Ziel) gleich aussehen. */
export interface TrackingMetrics {
  impressions: number;
  clicks: number;
  views: number;
  /** Kalender-Downloads (.ics). Wird serverseitig gezählt, nicht über /track gemeldet. */
  downloads: number;
  uniqueVisitors: number;
  uniqueClickers: number;
}

/** Ein Ziel innerhalb einer Entität, z. B. 'anmeldung', 'ical', 'landing-desktop'. */
export interface TrackingBreakdown extends TrackingMetrics {
  context: string | null;
}

export interface TrackingStat extends TrackingMetrics {
  entityType: string;
  entityId: string;
  /** Event-/News-Titel, sofern es einen gibt — sonst zeigt die Tabelle die ID. */
  entityLabel: string | null;
  /** ISO-Datum: bei News der Veröffentlichungstag, bei Events der Veranstaltungstag. */
  entityDate: string | null;
  /**
   * Aufschlüsselung nach Ziel. Die Zahlen der Entität sind NICHT deren Summe: Personen werden
   * über die ganze Entität gezählt, sonst zählte jemand doppelt, der klickt und dann liest.
   */
  breakdown: TrackingBreakdown[];
}

/** Fire-and-forget: Tracking darf nie die Seite stören oder Fehler werfen. */
export function track(
  entityType: TrackingEntityType,
  entityId: string,
  action: TrackingAction,
  context?: string
): void {
  try {
    api.post('/track', { entityType, entityId, action, context }).catch(() => {});
  } catch {
    // Auch ein unvollständig initialisierter Client oder Test-Mock darf den Produktweg nie stoppen.
  }
}

const seen = new Set<string>();

/** Wie track(), aber höchstens einmal pro Seitenaufruf (für Impressionen/Views bei Re-Renders). */
export function trackOnce(
  entityType: TrackingEntityType,
  entityId: string,
  action: TrackingAction,
  context?: string
): void {
  const key = `${entityType}:${entityId}:${action}`;
  if (seen.has(key)) return;
  seen.add(key);
  track(entityType, entityId, action, context);
}

export type OrganizerFunnelStep =
  | 'offer_viewed'
  | 'offer_selected'
  | 'organizer_selected'
  | 'organizer_registration_submitted'
  | 'claim_started'
  | 'claim_completed'
  | 'premium_checkout_started'
  | 'premium_requested'
  | 'promotion_checkout_started'
  | 'promotion_requested';

/**
 * Woher ein Besucher auf die Angebotsseite kam, als `?via=` im Link (`/fuer-veranstalter?via=banner`).
 * Nur `offer_viewed` trägt das Segment; die späteren Stufen erben es nicht, dafür gibt es
 * `via-angebot`. Geschlossene Liste, damit im Kontext nichts Fremdes aus der Adresszeile landet.
 */
/** `kopfzeile`: die Zeile „Das ist euer Verein?“ oben auf der Veranstalterseite (28.10.2026). */
export type OrganizerFunnelEntry = 'banner' | 'kopfzeile';
const FUNNEL_ENTRIES: OrganizerFunnelEntry[] = ['banner', 'kopfzeile'];
export function organizerFunnelEntry(value: string | null): OrganizerFunnelEntry | undefined {
  return FUNNEL_ENTRIES.find(e => e === value);
}

/**
 * Merker: Hat dieser Besucher heute die Seite „Für Veranstalter" gesehen?
 *
 * Ohne ihn mischt der Trichter zwei Grundgesamtheiten. `offer_viewed` entsteht nur auf
 * `/fuer-veranstalter`, die späteren Stufen dagegen überall — aus der Outreach-Mail direkt auf
 * die Veranstalterseite, über „Bist du der Veranstalter?" auf der Event-Seite, aus dem
 * Veranstalter-Bereich heraus. Eine Quote „von allen, die das Angebot gesehen haben" war damit
 * nicht zu rechnen, sie konnte rechnerisch über 100 % gehen.
 *
 * **Tagesgenau, mit Absicht**: der Besucher-Hash im Backend wird täglich neu gebildet
 * (`TrackingService.visitorHash`). Ein Merker, der länger gilt, würde Zähler und Nenner
 * auseinanderziehen.
 */
const OFFER_SEEN_KEY = 'rse-angebot-gesehen';

const heute = (): string => new Date().toISOString().slice(0, 10);

/** Fire-and-forget: ohne localStorage (privates Fenster) zählt der Besuch eben als „direkt". */
function markOfferSeen(): void {
  try {
    localStorage.setItem(OFFER_SEEN_KEY, heute());
  } catch {
    /* Tracking darf nie die Seite stören */
  }
}

function offerSeenToday(): boolean {
  try {
    return localStorage.getItem(OFFER_SEEN_KEY) === heute();
  } catch {
    return false;
  }
}

/**
 * Der Veranstalter-Funnel in First-Party-Tracking und Umami.
 *
 * Es werden nur Produkt, Schritt und technische Ids gemeldet, niemals Namen, Mailadressen oder
 * Rechnungsdaten. So lässt sich der Weg von der Angebotsseite bis zur Anfrage als Quote lesen.
 *
 * Jedes Ereignis trägt als letztes Segment seine Herkunft: `via-angebot`, wenn der Besucher heute
 * schon auf der Angebotsseite war, sonst `via-direkt`. Ereignisse von vor dem 18.09.2026 haben
 * kein solches Segment und zählen in der Auswertung als „Herkunft unbekannt".
 */
export function trackOrganizerFunnel(
  step: OrganizerFunnelStep,
  data: { offer?: 'free' | 'premium' | 'promotion'; organizerId?: number; result?: string; entry?: OrganizerFunnelEntry } = {},
  once = false,
): void {
  if (step === 'offer_viewed') markOfferSeen();
  const herkunft = step === 'offer_viewed' || offerSeenToday() ? 'via-angebot' : 'via-direkt';
  // Der Eingang steht *vor* der Herkunft: das Backend erkennt die Herkunft am Satzende
  // (`LIKE '%:via-angebot'`), ein Segment dahinter würde alle Herkünfte unlesbar machen.
  const eingang = data.entry ? `von-${data.entry}` : null;
  const context = [step, data.offer, data.result, data.organizerId != null ? `o${data.organizerId}` : null, eingang, herkunft]
    .filter(Boolean).join(':');
  if (once) trackOnce('PAGE', 'organizer-monetization', 'VIEW', context);
  else track('PAGE', 'organizer-monetization', 'CLICK', context);
  umamiTrack('veranstalter-funnel', {
    schritt: step,
    herkunft,
    ...(data.entry ? { eingang: data.entry } : {}),
    ...(data.offer ? { angebot: data.offer } : {}),
    ...(data.organizerId != null ? { veranstalter: data.organizerId } : {}),
    ...(data.result ? { ergebnis: data.result } : {}),
  });
}

// ── Convenience-Helper: melden an BEIDE Systeme ─────────────────────────────────
// First-Party-DB (belastbare Zahlen für Partner-Reporting, adblocker-resistent)
// und Umami (primäres Auswertungstool, Dashboards/Funnels).

/** Werbebanner wurde angezeigt. Einmal pro Seitenaufruf via trackOnce absichern! */
export function trackBannerImpression(slot: string, placement: string): void {
  trackOnce('AD_BANNER', slot, 'IMPRESSION', placement);
  umamiTrack('banner-impression', { banner: slot, platzierung: placement });
}

/** Werbebanner wurde geklickt. */
export function trackBannerClick(slot: string, placement: string): void {
  track('AD_BANNER', slot, 'CLICK', placement);
  umamiTrack('banner-klick', { banner: slot, platzierung: placement });
}

/** News-Artikel wurde in einer Liste angeklickt. */
export function trackNewsClick(slug: string, placement: string): void {
  track('NEWS', slug, 'CLICK', placement);
  umamiTrack('news-klick', { artikel: slug, platzierung: placement });
}

/** Admin: aggregierte Statistiken (Standard: letzte 30 Tage). */
export async function getTrackingStats(params: {
  from?: string;
  to?: string;
  entityType?: string;
}): Promise<TrackingStat[]> {
  const res = await api.get('/admin/stats/tracking', { params });
  return res.data;
}
